import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { CurrencyInput } from "@/components/CurrencyInput";
import { toast } from "sonner";
import { Undo2, Plus, Trash2 } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

const emptyLine = { inventory_id: "", nama_produk: "", qty: 1 };

const TIPE_CLS = {
  refund: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  return: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
};

export default function Returns() {
  const { isAdmin } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [returns, setReturns] = useState([]);
  const [tipe, setTipe] = useState("refund");
  const [pelanggan, setPelanggan] = useState("");
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [nominal, setNominal] = useState(0);
  const [restock, setRestock] = useState(true);
  const [alasan, setAlasan] = useState("");
  const [saving, setSaving] = useState(false);

  const loadInv = useCallback(async () => { const { data } = await api.get("/inventory"); setInventory(data); }, []);
  const loadReturns = useCallback(async () => { const { data } = await api.get("/returns"); setReturns(data); }, []);

  useEffect(() => { loadInv(); loadReturns(); }, [loadInv, loadReturns]);

  const addLine = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const updLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    const items = lines.filter((l) => l.nama_produk.trim()).map((l) => ({ ...l, qty: Number(l.qty) || 1 }));
    setSaving(true);
    try {
      await api.post("/returns", {
        tipe, nama_pelanggan: pelanggan, items,
        nominal_refund: Number(nominal || 0), restock, alasan,
      });
      toast.success(tipe === "refund" ? "Refund tercatat" : "Retur tercatat");
      setPelanggan(""); setLines([{ ...emptyLine }]); setNominal(0); setRestock(true); setAlasan("");
      loadInv(); loadReturns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus data ini? Jika ada restock, stok akan disesuaikan kembali.")) return;
    await api.delete(`/returns/${id}`);
    toast.success("Data dihapus");
    loadInv(); loadReturns();
  };

  return (
    <div>
      <PageHeader title="Refund & Retur" subtitle="Pengembalian Dana / Barang" />

      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <div className="flex gap-1.5 mb-4">
          {[{ k: "refund", label: "Refund (uang kembali)" }, { k: "return", label: "Retur (barang kembali)" }].map((t) => (
            <button key={t.k} data-testid={`return-tipe-${t.k}`} onClick={() => setTipe(t.k)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${tipe === t.k ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40" : "bg-slate-800/50 text-slate-400 border border-transparent hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <input value={pelanggan} onChange={(e) => setPelanggan(e.target.value)} placeholder="Nama pelanggan" className={`${inputCls} mb-4`} data-testid="return-pelanggan" />

        <div className="rounded-lg border border-slate-800 bg-slate-950/30 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-heading font-semibold text-white text-sm">Barang Dikembalikan</h4>
            <button onClick={addLine} data-testid="return-add-line" className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"><Plus className="w-3.5 h-3.5" /> Tambah item</button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <select
                  value={l.inventory_id}
                  onChange={(e) => { const inv = inventory.find((x) => x.id === e.target.value); updLine(i, inv ? { inventory_id: e.target.value, nama_produk: inv.nama_produk } : { inventory_id: e.target.value }); }}
                  className={`${inputCls} col-span-5`}
                  data-testid={`return-inv-${i}`}
                >
                  <option value="">— Manual —</option>
                  {inventory.map((it) => <option key={it.id} value={it.id}>{it.nama_produk} (stok {it.stok_qty})</option>)}
                </select>
                <input value={l.nama_produk} onChange={(e) => updLine(i, { nama_produk: e.target.value })} placeholder="Nama produk" className={`${inputCls} col-span-4`} />
                <input type="number" inputMode="numeric" value={l.qty} onChange={(e) => updLine(i, { qty: e.target.value })} placeholder="Qty" className={`${inputCls} col-span-2 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`} title="Qty" />
                <button onClick={() => remLine(i)} className="col-span-1 text-slate-500 hover:text-rose-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Nominal Refund</label>
            <CurrencyInput value={nominal} onChange={setNominal} className={inputCls} data-testid="return-nominal" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer pb-2" data-testid="return-restock">
            <input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} className="w-4 h-4 accent-indigo-500" />
            Kembalikan ke stok (restock)
          </label>
          <input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="Alasan (opsional)" className={inputCls} />
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition disabled:opacity-60 shadow-lg shadow-indigo-600/30" data-testid="submit-return-button">
            <Undo2 className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800"><h3 className="font-heading font-semibold text-white">Riwayat Refund & Retur</h3></div>
        {returns.length === 0 ? (
          <EmptyState icon={Undo2} title="Belum ada data" subtitle="Catat refund (pengembalian dana) atau retur (barang kembali) di sini." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="returns-table">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Kode</th>
                  <th className="text-left px-5 py-3">Tanggal</th>
                  <th className="text-left px-5 py-3">Tipe</th>
                  <th className="text-left px-5 py-3">Pelanggan</th>
                  <th className="text-left px-5 py-3">Barang</th>
                  <th className="text-right px-5 py-3">Refund</th>
                  <th className="text-center px-5 py-3">Restock</th>
                  {isAdmin && <th className="px-5 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {returns.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3 font-mono text-xs text-indigo-300">{r.kode}</td>
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                    <td className="px-5 py-3"><Badge className={TIPE_CLS[r.tipe]}><span className="capitalize">{r.tipe}</span></Badge></td>
                    <td className="px-5 py-3 text-white">{r.nama_pelanggan || "-"}</td>
                    <td className="px-5 py-3 text-slate-300">{(r.items || []).map((i) => `${i.nama_produk} ×${i.qty}`).join(", ") || "-"}</td>
                    <td className="px-5 py-3 text-right font-mono text-amber-300">{formatRupiah(r.nominal_refund)}</td>
                    <td className="px-5 py-3 text-center">{r.restock ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Ya</Badge> : <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/30">Tidak</Badge>}</td>
                    {isAdmin && <td className="px-5 py-3 text-right"><button onClick={() => del(r.id)} data-testid={`delete-return-${r.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
